import React, { ChangeEvent, useCallback } from 'react'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Select } from 'transfix-ui/components/Select'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { Grid, Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { DateRangeFilter } from 'apps/tms/components/Insights/Filters/DateRangeFilter'
import { IDateRange } from 'apps/tms/components/Insights/types'
import { AutocompleteFacilities } from '../AutocompleteFacilities'

const useFacilitiesFilterStyles = makeStyles(({ spacing }) => ({
  root: {
    marginBottom: spacing(4),
    background: brandColors.coolGray0,
  },
}))

interface IFacilitiesFilterProps {
  dateRangeSelection: IDateRange
  facilities: IAutocompleteBaseOption[]
  facilitySelection: string
  isLoading: boolean
  loadingMethods: TSelectOpt[]
  loadingMethodSelection?: string
  modes: TSelectOpt[]
  modeSelection?: string
  onDateRangeSelectionChange: (value: IDateRange) => void
  onFacilityChange: (value: IAutocompleteBaseOption) => void
  onLoadingMethodChange: (event: ChangeEvent) => void
  onModeChange: (event: ChangeEvent) => void
  onReset: () => void
  showReset?: boolean
}

export const FacilitiesFilter = (props: IFacilitiesFilterProps) => {
  const {
    dateRangeSelection,
    facilities,
    facilitySelection,
    isLoading,
    loadingMethodSelection,
    loadingMethods,
    modeSelection,
    modes,
    onDateRangeSelectionChange,
    onFacilityChange,
    onLoadingMethodChange,
    onModeChange,
    onReset,
    showReset,
  } = props

  const classes = useFacilitiesFilterStyles()

  const autocompleteHandler = useCallback(
    (_, { id }) => {
      onFacilityChange(id)
    },
    [onFacilityChange]
  )

  const loadingMethodSelectHandler = useCallback(
    ({ target }) => {
      onLoadingMethodChange(target.value)
    },
    [onLoadingMethodChange]
  )

  const modesSelectHandler = useCallback(
    ({ target }) => {
      onModeChange(target.value)
    },
    [onModeChange]
  )

  return (
    <Card className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <AutocompleteFacilities
            facilities={facilities}
            facilitySelection={facilitySelection}
            isLoading={isLoading}
            onAutocompleteChange={autocompleteHandler}
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            large
            value={loadingMethodSelection}
            onChange={loadingMethodSelectHandler}
            options={loadingMethods}
            dataTest='Transfix-Shipper-App-LoadingMethodsSelect'
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            large
            value={modeSelection}
            onChange={modesSelectHandler}
            options={modes}
            dataTest='Transfix-Shipper-App-ModesSelect'
          />
        </Grid>
        <Grid item xs={12}>
          <Box display='flex' justifyContent='space-between'>
            <DateRangeFilter value={dateRangeSelection} onChange={onDateRangeSelectionChange} />
            {showReset && (
              <Button
                onClick={onReset}
                color='secondary'
                data-test='Transfix-Shipper-App-ResetFilter'>
                Reset Filter
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  )
}
